import React, { useCallback, forwardRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import moduleSchema from '@galo-monitor/forms/schemas/root/module.schema'
import { useT } from '@galo-monitor/i18n'
import { useSnackbar } from '@galo-monitor/snackbar'
import { getContextIdBySlug, selectContexts } from '@galo-monitor/stores/context/selectorContext'
import { ModuleActions } from '@galo-monitor/stores/module'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'

import { Container } from './styles'

const ModuleForm = forwardRef((props, formRef) => {
  const { mod, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()
  const contexts = useSelector(selectContexts)

  const contextOptions = useMemo(
    () => map(contexts, item => ({ value: item.contextId.toString(), label: item.name })),
    [contexts]
  )

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)
      dispatch(ModuleActions.saveModule(
        { ...mod, ...data },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('module', { howMany: 1 }),
            action: toLower(t(isEmpty(mod) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [dispatch, mod, onError, onSuccess, setLoading, snackbar, t]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ moduleSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <InputText
          name="name"
          label={ t('name') }
          defaultValue={ mod.name }
          disabled={ loading }
          fullWidth
        />

        <InputSelect
          name="context_id"
          label={ t('context') }
          defaultValue={ (getContextIdBySlug(mod.contextSlug) || '').toString() }
          options={ contextOptions }
          disabled={ loading }
          fullWidth
        />
      </Container>
    </Form>
  )
})

ModuleForm.propTypes = {
  mod: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

ModuleForm.defaultProps = {
  mod: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default ModuleForm
